<template>
  <div class="settings-view">
    <div class="container pt-3 pt-xl-5">
      <div class="card p-4 mb-3">
        <h3 class="mb-1 mb-sm-4">
          {{ $t("settings.title") }}
        </h3>
        <div v-if="loaded">
          <div class="mb-3">
            <label for="inputName" class="form-label">
              {{ $t("settings.name") }}
            </label>
            <input
              id="inputName"
              v-model="localName"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': v$.localName.$error }"
              @blur="v$.localName.$touch()"
            />
            <div v-if="v$.localName.$error" class="invalid-feedback">
              {{ v$.localName.$errors[0].$message }}
            </div>
          </div>

          <div class="mb-3">
            <label for="inputEmail" class="form-label">
              {{ $t("settings.email") }}
            </label>
            <input
              id="inputEmail"
              :value="accountData.email"
              type="email"
              class="form-control"
              disabled
              readonly
            />
            <small class="form-text text-muted">
              {{ $t("settings.email_cannot_be_changed") }}
            </small>
          </div>

          <div class="mb-3">
            <label for="inputPhone" class="form-label">
              {{ $t("settings.phone") }}
            </label>
            <input
              id="inputPhone"
              v-model="localPhone"
              type="tel"
              class="form-control"
              :class="{ 'is-invalid': v$.localPhone.$error }"
              @blur="v$.localPhone.$touch()"
            />
            <div v-if="v$.localPhone.$error" class="invalid-feedback">
              {{ v$.localPhone.$errors[0].$message }}
            </div>
          </div>

          <div class="mb-3">
            <label for="selectLanguage" class="form-label">
              {{ $t("settings.select_language") }}
            </label>
            <select
              id="selectLanguage"
              v-model="localLanguage"
              class="form-select"
              role="button"
            >
              <option value="" disabled>
                {{ $t("settings.select_language") }}
              </option>
              <option value="en">{{ $t("language.en") }}</option>
              <option value="et">{{ $t("language.et") }}</option>
              <option value="lv">{{ $t("language.lv") }}</option>
              <option value="lt">{{ $t("language.lt") }}</option>
              <option value="sv">{{ $t("language.sv") }}</option>
            </select>
          </div>

          <div
            class="mb-3 setting-highlightable"
            :class="{ 'setting-highlight': highlightTransactionEmails }"
          >
            <div class="form-check form-switch">
              <input
                id="receiveTransactionEmails"
                v-model="localReceiveTransactionEmails"
                class="form-check-input"
                type="checkbox"
                role="switch"
              />
              <label class="form-check-label" for="receiveTransactionEmails">
                {{ $t("settings.receive_transaction_emails") }}
              </label>
            </div>
            <div class="form-text">
              {{ $t("settings.receive_transaction_emails_hint") }}
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-between w-100">
        <button @click="redirectToPreviousPage" class="btn btn-secondary me-2">
          {{ $t("common.back") }}
        </button>
        <button
          @click="saveAndRedirect"
          class="btn btn-primary"
          :disabled="!hasChanges || v$.$invalid || isSaving"
        >
          <span v-if="isSaving">
            <span
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            {{ $t("common.saving") }}
          </span>
          <span v-else>
            {{ $t("common.save") }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAccount } from "@/composables/account/useAccount";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useQueryManager } from "@/composables/useQueryManager";
import { useValidators } from "@/composables/useValidators";
import router from "@/router";
import type { Account } from "@/types";
import { useVuelidate } from "@vuelidate/core";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

// How long the ?highlight=<setting> emphasis stays on before fading (email deep links land here).
const HIGHLIGHT_DURATION_MS = 4000;

const { t } = useI18n();
const validators = useValidators(t);
const { globalErrorHandler } = useGlobalHandlers();
const { getQueryValue, parsers } = useQueryManager();

const accountStore = useAccountStore();
// The form is filled once this page's load lands, not from whatever account an earlier page cached.
const { accountData, loaded } = useAccount(initializeForm);

const localName = ref<string | null>(null);
const localPhone = ref<string | null>(null);
const localLanguage = ref<string>("");
const localReceiveTransactionEmails = ref(true);
const isSaving = ref(false);

// Unknown or absent ?highlight values are no-ops, so the email link degrades to "the settings page".
const highlightParam = getQueryValue("highlight", parsers.string);
const highlightTransactionEmails = ref(false);

const rules = {
  localName: {
    required: validators.required,
    personalName: validators.personalName,
  },
  localPhone: {
    phoneNumber: validators.phoneNumber,
  },
};

const v$ = useVuelidate(rules, { localName, localPhone });

const hasChanges = computed(() => {
  return (
    localName.value !== accountData.name ||
    localPhone.value !== accountData.phone_number ||
    localLanguage.value !== accountData.language ||
    localReceiveTransactionEmails.value !==
      !accountData.transaction_emails_unsubscribed
  );
});

const redirectToPreviousPage = () => {
  const previousRoute = router.options.history.state.back;
  if (previousRoute) {
    router.go(-1);
  } else {
    router.push("/");
  }
};

const saveAndRedirect = async () => {
  v$.value.$touch();
  if (v$.value.$invalid || isSaving.value) {
    return;
  }

  isSaving.value = true;
  try {
    await accountStore.postAccountData("/api/account/update", {
      ...accountData,
      name: localName.value,
      phone_number: localPhone.value?.trim() || null,
      language: localLanguage.value,
      transaction_emails_unsubscribed: !localReceiveTransactionEmails.value,
    });
    redirectToPreviousPage();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSaving.value = false;
  }
};

function initializeForm(account: Readonly<Account>) {
  localName.value = account.name;
  localPhone.value = account.phone_number;
  localLanguage.value = account.language ?? "";
  localReceiveTransactionEmails.value =
    !account.transaction_emails_unsubscribed;

  if (highlightParam.value === "transaction_emails") {
    highlightTransactionEmails.value = true;
    setTimeout(() => {
      highlightTransactionEmails.value = false;
    }, HIGHLIGHT_DURATION_MS);
  }
}
</script>

<style scoped lang="scss">
.settings-view {
  overflow-y: auto;

  .container {
    max-width: 800px;
  }
}

.setting-highlightable {
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  margin-left: -0.75rem;
  margin-right: -0.75rem;
  transition: background-color 1.5s ease;
}

.setting-highlight {
  background-color: #fff3cd;
}
</style>
